import { Component, inject, OnInit } from '@angular/core';
import { BookingIncidentType } from '@luggagehero/shared/interfaces';
import { SharedNotificationService } from '@luggagehero/shared/services/notification';
import { hasPricedCancellationFee } from '@luggagehero/shared/util';
import { TranslateService } from '@ngx-translate/core';

import { BookingActionBaseComponent } from './booking-action.base-component';

const MIN_COMMENT_LENGTH = 15;
const DISABLED_INCIDENT_TYPES: BookingIncidentType[] = [BookingIncidentType.UnableToFindLocation];

@Component({
  template: '',
  standalone: false,
})
export abstract class ReportIncidentActionBaseComponent extends BookingActionBaseComponent implements OnInit {
  /**
   * Which "cancel the booking too?" prompt the incident flow shows (AM-4788 round 8).
   *
   * Master's copy promised, unconditionally, that the traveler would not be charged. That promise is
   * simply TRUE wherever no cancellation fee is priced — nothing is ever charged for cancelling —
   * so a booking with no priced fee keeps master's wording exactly. Where a fee IS priced the
   * unconditional promise would be wrong for ordinary cancellations, and the newer key scopes it to
   * the incident case, which the server does force free.
   */
  public get cancelTooPromptKey(): string {
    return hasPricedCancellationFee(this.booking?.orderDetails?.[0]?.productList?.products)
      ? 'DO_YOU_WANT_TO_CANCEL_YOUR_BOOKING_TOO'
      : 'DO_YOU_WANT_TO_CANCEL_YOUR_BOOKING';
  }
  private translate = inject(TranslateService);
  private notificationService = inject(SharedNotificationService);

  public selectedIncident: BookingIncidentType;
  public isIncidentSelected = false;
  public acceptIncidentImage = false;
  public showStorageLocationTelephone = false;
  public isCancelBookingSelected = false;
  public incidentTypes = Object.values(BookingIncidentType).filter((i) => !DISABLED_INCIDENT_TYPES.includes(i));
  public comments: string;
  public showComments = false;
  public imageName: string = null;
  public remoteIncidentImage: string;
  public localIncidentImage: string;
  public storageLocationTelephoneNumber: string;

  public constructor() {
    super();
    this.selectedIncident = this.translate.instant('WHAT_HAPPENED') as BookingIncidentType;
  }

  ngOnInit() {
    super.ngOnInit();
    void this.shopsService.getShop(this.booking.shopId).then((s) => (this.storageLocationTelephoneNumber = s.phone));
  }

  public get isSubmitAllowed(): boolean {
    if (this.isLoading) {
      return false;
    }
    if (this.acceptIncidentImage && !this.localIncidentImage) {
      return false;
    }
    return this.isIncidentSelected && this.comments && this.comments.length >= MIN_COMMENT_LENGTH;
  }

  public get isCancelBookingVisible(): boolean {
    if (!this.isIncidentSelected) {
      return false;
    }
    return ['CONFIRMED', 'CHECKED_IN'].includes(this.booking.status);
  }

  public get isLegacyBooking(): boolean {
    return this.booking?.schemaVersion < 3;
  }

  public get commentsPlaceholder(): string {
    const comments = this.translate.instant('COMMENTS') as string;
    const minChars = (this.translate.instant('MIN_15_CHARACTERS') as string).toLowerCase();
    return `${comments} (${minChars})`;
  }

  public selectIncident(value: BookingIncidentType) {
    this.selectedIncident = value;
    this.isIncidentSelected = true;

    if (this.selectedIncident === BookingIncidentType.UnableToFindLocation) {
      this.acceptIncidentImage = false;
      if (this.storageLocationTelephoneNumber) {
        this.showStorageLocationTelephone = true;
      }
    } else {
      this.acceptIncidentImage = true;
      this.showStorageLocationTelephone = false;
    }

    this.cd.markForCheck();
  }

  onLocalImageLoad() {
    URL.revokeObjectURL(this.localIncidentImage);
  }

  public async submitIncidentReport() {
    this.isLoading = true;

    try {
      let images: string[] = null;
      if (this.imageName) {
        images = [this.imageName];
      }
      const res = await this.bookingService.reportIncident(
        this.booking._id,
        this.selectedIncident,
        this.comments,
        images,
        this.isCancelBookingSelected,
        this.isLegacyBooking,
      );

      this.notificationService.chat(
        this.translate.instant('INCIDENT_REPORT_SUCCESS_BODY') as string,
        this.translate.instant('INCIDENT_REPORT_SUCCESS_TITLE') as string,
      );

      if (this.isCancelBookingSelected && res.status !== 'CANCELLED') {
        const cancelError = this.translate.instant('CANCEL_FAILED') as string;
        this.notificationService.error(cancelError);
      }

      this.updateBooking(res);
      this.close();
    } catch (err) {
      // TODO: Handle errors better
      this.notificationService.error((err as { message?: string }).message);
    }
    this.isLoading = false;
  }
}
